import React, { useEffect, useMemo } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, Clock, ChevronRight } from "lucide-react";
import DocsNavigation from "../components/landing/DocsNavigation";
import { Footer } from "../components/landing/Footer";
import {
  CATEGORIES,
  getArticlesByCategory,
} from "../data/helpArticles";

const HelpCategoryPage = () => {
  const { categoryId } = useParams();
  const category = useMemo(
    () => CATEGORIES.find((c) => c.id === categoryId),
    [categoryId]
  );
  const articles = useMemo(
    () => getArticlesByCategory(categoryId),
    [categoryId]
  );

  useEffect(() => {
    const html = document.documentElement;
    const wasLight = html.classList.contains("light");
    if (wasLight) {
      html.classList.remove("light");
      html.classList.add("dark");
    }
    window.scrollTo(0, 0);
    return () => {
      if (wasLight) {
        html.classList.remove("dark");
        html.classList.add("light");
      }
    };
  }, []);

  if (!category) {
    return <Navigate to="/help" replace />;
  }

  return (
    <div
      className="min-h-screen bg-space-900 relative"
      data-testid="help-category-page"
    >
      <DocsNavigation />

      <main className="relative pt-32 pb-20 px-6 md:px-12">
        <div className="max-w-4xl mx-auto">
          <Link
            to="/help"
            className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-teal-400 transition-colors mb-6"
            data-testid="help-category-back-link"
          >
            <ArrowLeft size={16} />
            All categories
          </Link>

          <header className="mb-10 pb-8 border-b border-white/[0.06]">
            <h1 className="font-display font-semibold text-4xl sm:text-5xl text-white mb-3">
              {category.title}
            </h1>
            <p className="text-gray-400 text-lg max-w-2xl">
              {category.description}
            </p>
          </header>

          {articles.length === 0 ? (
            <p className="text-gray-500">
              No articles yet in this category. We're writing them as we speak —
              check back soon, or{" "}
              <Link to="/support" className="text-teal-400 hover:underline">
                contact support
              </Link>{" "}
              if you need help right now.
            </p>
          ) : (
            <div className="space-y-3">
              {articles.map((a) => (
                <Link
                  key={a.slug}
                  to={`/help/${a.slug}`}
                  className="group flex items-start justify-between p-5 rounded-xl bg-white/[0.03] border border-white/[0.06] hover:border-teal-400/30 hover:bg-white/[0.05] transition-all"
                  data-testid={`help-category-article-${a.slug}`}
                >
                  <div className="flex-1 min-w-0 pr-4">
                    <div className="text-white font-semibold mb-1">
                      {a.title}
                    </div>
                    <div className="text-sm text-gray-400 leading-relaxed">
                      {a.description}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-500 mt-2">
                      <Clock size={11} />
                      {a.readTime} min read
                    </div>
                  </div>
                  <ChevronRight
                    size={18}
                    className="text-gray-500 group-hover:text-teal-300 group-hover:translate-x-1 transition-all flex-shrink-0 mt-1"
                  />
                </Link>
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default HelpCategoryPage;
